import {combineReducers} from "redux";
import {IRoutingState} from "./routing/types";
import {routingReducer} from "./routing/reducer";
import {IGameState} from "./game/types";
import {gameReducer} from "./game/reducer";
import {ISettingsState} from "./settings/types";
import {settingsReducer} from "./settings/reducer";
import settingsSaga from "./settings/sagas";
import {loadingReducer} from "./common/loadingReducer";
import {IPlayerState} from "./player/types";
import {playerReducer} from "./player/reducer";
import {all, call, CallEffectFn, put, spawn, take} from "redux-saga/effects";
import gameSaga from "./game/sagas";
import {IMiniGamesState, miniGamesReducer, miniGamesSaga} from "./mini-games";
import playerSaga from "./player/sagas";
import {IQuestionState} from "./questions/types";
import {questionsReducer} from "./questions/reducer";
import questionsSaga from "./questions/sagas";
import {IHintsState} from "./hints/types";
import {hintsReducer} from "./hints/reducer";
import hintsSaga from "./hints/sagas";
import {IDialogManagerState} from "./dialog-manager/types";
import {dialogManagerReducer} from "./dialog-manager/reducer";
import dialogManagerSaga from "./dialog-manager/sagas";
import commonSagas from "./common/sagas";
import routerSaga from "./routing/sagas";
import {ITutorialState} from "./tutorial/types";
import {tutorialReducer} from "./tutorial/reducer";
import tutorialSaga from "./tutorial/sagas";
import {IAdvertisementState} from "./advertisement/types";
import {advertisementReducer} from "./advertisement/reducer";
import advertisementSaga from "./advertisement/sagas";
import {IErrorState, LogLevel} from "./log/types";
import {logReducer} from "./log/reducer";
import logSaga from "./log/sagas";
import {trackError} from "./log/actions";
import {closeDialog, showDialog} from "./dialog-manager/actions";
import {getType} from "typesafe-actions";
import {loadScene} from "./routing/actions";
import {IPaymentsState} from "./payments/types";
import {paymentsReducer} from "./payments/reducer";
import paymentsSaga from "./payments/sagas";
import i18n from "../shared/i18n";

export interface IApplicationState {
    readonly settings: ISettingsState,
    readonly game: IGameState,
    readonly player: IPlayerState,
    readonly routing: IRoutingState,
    readonly loading: Record<string, boolean>,
    readonly log: IErrorState,
    readonly miniGames: IMiniGamesState,
    readonly questions: IQuestionState,
    readonly hints: IHintsState,
    readonly dialogs: IDialogManagerState,
    readonly tutorial: ITutorialState,
    readonly advertisement: IAdvertisementState,
    readonly payments: IPaymentsState,
}

export default combineReducers<IApplicationState>({
    settings: settingsReducer,
    game: gameReducer,
    player: playerReducer,
    routing: routingReducer,
    loading: loadingReducer,
    log: logReducer,
    miniGames: miniGamesReducer,
    questions: questionsReducer,
    hints: hintsReducer,
    dialogs: dialogManagerReducer,
    tutorial: tutorialReducer,
    advertisement: advertisementReducer,
    payments: paymentsReducer,
});

export function* rootSaga() {
    const sagas: [string, CallEffectFn<() => any>][] = [
        ['settingsSaga', settingsSaga],
        ['gameSaga', gameSaga],
        ['miniGamesSaga', miniGamesSaga],
        ['playerSaga', playerSaga],
        ['questionsSaga', questionsSaga],
        ['hintsSaga', hintsSaga],
        ['dialogManagerSaga', dialogManagerSaga],
        ['commonSagas', commonSagas],
        ['routerSaga', routerSaga],
        ['advertisementSaga', advertisementSaga],
        ['logSaga', logSaga],
        ['tutorialSaga', tutorialSaga],
        ['paymentsSaga', paymentsSaga],
    ];

    yield all(
        sagas.map(([sagaName, saga]) =>
            spawn(function* () {
                while (true) {
                    try {
                        yield call(saga);
                        break;
                    } catch (error) {
                        yield put(trackError({
                            logLevel: LogLevel.Fatal,
                            category: "rootSaga",
                            message: `Uncaught error in ${sagaName}`,
                            error: error,
                        }));

                        yield put(showDialog({
                            type: 'NotificationDialog',
                            options: {
                                title: i18n("loadingcover.failed"),
                                text: i18n("loadingcover.failed.text")
                            }
                        }));

                        yield take(getType(closeDialog));

                        yield put(loadScene.request('MainMenu'));
                    }
                }
            })
        ));
}